import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { withdrawalStatusToBadge, formatStatusLabel } from '@/shared/utils/statusBadge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFinanceApi, adminFinanceKeys } from '@/features/admin-finance/api/admin-finance.api';
import { formatCurrency } from '@/features/guide-finance/utils/formatCurrency';
import {
  financeListSx,
  financeRowSx,
  financeToolbarSx,
} from '@/features/admin-finance/styles/financeSurfaces';

export function FinanceWithdrawalsPanel() {
  const [status, setStatus] = useState('PENDING');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: adminFinanceKeys.withdrawals({ status }),
    queryFn: () => adminFinanceApi.listWithdrawals({ status, limit: 50 }),
  });

  const approve = useMutation({
    mutationFn: (id: string) => adminFinanceApi.approveWithdrawal(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminFinanceKeys.all }),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminFinanceApi.rejectWithdrawal(id, reason),
    onSuccess: () => {
      setRejectId(null);
      setRejectReason('');
      void qc.invalidateQueries({ queryKey: adminFinanceKeys.all });
    },
  });

  const complete = useMutation({
    mutationFn: (id: string) => adminFinanceApi.completeWithdrawal(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminFinanceKeys.all }),
  });

  const withdrawals = data?.data ?? [];

  return (
    <Box sx={financeListSx}>
      <Box sx={financeToolbarSx}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['PENDING', 'APPROVED', 'COMPLETED', 'REJECTED'].map((s) => (
            <Button
              key={s}
              size="small"
              variant={status === s ? 'contained' : 'outlined'}
              onClick={() => setStatus(s)}
              sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1.5 }}
            >
              {s}
            </Button>
          ))}
        </Box>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0, m: 2 }}>
          Failed to load withdrawals.
        </Alert>
      )}
      {!isLoading && !error && withdrawals.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 0, m: 2 }}>
          No withdrawals in this status.
        </Alert>
      )}

      {withdrawals.map((w) => (
        <Box key={w.id} sx={financeRowSx}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography fontWeight={700} letterSpacing="-0.02em">
                {formatCurrency(w.amount, w.currency)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Guide {w.guideId.slice(-6)} · {new Date(w.requestedAt).toLocaleString()}
              </Typography>
              <StatusBadge
                status={withdrawalStatusToBadge(w.status)}
                label={formatStatusLabel(w.status)}
                sx={{ mt: 1, borderRadius: 0 }}
              />
              {w.adminRemarks && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Note: {w.adminRemarks}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {w.status === 'PENDING' && (
                <>
                  <Button size="small" variant="contained" sx={{ borderRadius: 0 }} onClick={() => approve.mutate(w.id)}>
                    Approve
                  </Button>
                  <Button size="small" color="error" sx={{ borderRadius: 0 }} onClick={() => setRejectId(w.id)}>
                    Reject
                  </Button>
                </>
              )}
              {w.status === 'APPROVED' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    sx={{ borderRadius: 0 }}
                    onClick={() => complete.mutate(w.id)}
                  >
                    Mark completed
                  </Button>
                  <Button size="small" color="error" sx={{ borderRadius: 0 }} onClick={() => setRejectId(w.id)}>
                    Reject
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      ))}

      <Dialog
        open={Boolean(rejectId)}
        onClose={() => setRejectId(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 0 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reject withdrawal</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectId(null)} sx={{ borderRadius: 0 }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ borderRadius: 0 }}
            disabled={!rejectReason.trim() || reject.isPending}
            onClick={() => {
              if (rejectId) reject.mutate({ id: rejectId, reason: rejectReason.trim() });
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
